import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider
} from '@mui/material'
import {
  Rocket as RocketIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  ArrowBack as BackIcon,
  Pix as PixIcon,
  Receipt as BoletoIcon
} from '@mui/icons-material'
import { useAuth } from '../../context/AuthContext'
import { getBoostPlans, createPayment } from '../../services/paymentService'

const BoostProperty = () => {
  const { propertyId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useAuth()

  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState('pix')
  const [creating, setCreating] = useState(false)

  // Pega o plano pré-selecionado se vier do modal
  const preSelectedPlanId = location.state?.selectedPlanId

  // Carregar planos
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true)
        const data = await getBoostPlans()
        setPlans(data)

        // Se veio com plano pré-selecionado, seleciona automaticamente
        if (preSelectedPlanId) {
          const plan = data.find(p => p.id === preSelectedPlanId)
          if (plan) {
            setSelectedPlan(plan)
          }
        }

        setError(null)
      } catch (err) {
        setError('Erro ao carregar planos de impulsionamento')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    loadPlans()
  }, [preSelectedPlanId])

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan)
  }

  const handleCreatePayment = async () => {
    if (!selectedPlan) {
      setError('Selecione um plano')
      return
    }

    try {
      setCreating(true)
      setError(null)

      const response = await createPayment(
        propertyId,
        selectedPlan.id,
        paymentMethod,
        token
      )

      // Redireciona para checkout com os dados do pagamento
      navigate(`/checkout/${response.payment.id}`, {
        state: {
          propertyId,
          planName: selectedPlan.name
        }
      })
    } catch (err) {
      setError(err.message || 'Erro ao criar pagamento')
      console.error(err)
    } finally {
      setCreating(false)
    }
  }

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'básico':
        return <TrendingIcon />
      case 'premium':
        return <StarIcon />
      case 'super':
        return <RocketIcon />
      default:
        return <TrendingIcon />
    }
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Impulsionar Anúncio
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Escolha um plano para destacar seu imóvel e alcançar mais interessados
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Planos */}
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Escolha seu Plano
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {plans.map((plan) => (
            <Grid size={{ xs: 12, md: 4 }} key={plan.id}>
              <Card
                sx={{
                  height: '100%',
                  border: selectedPlan?.id === plan.id ? 2 : 1,
                  borderColor: selectedPlan?.id === plan.id ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleSelectPlan(plan)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    {getPlanIcon(plan.plan_type)}
                    <Typography variant="h6" fontWeight={600}>
                      {plan.name}
                    </Typography>
                  </Box>

                  {plan.has_premium_badge && (
                    <Chip
                      label="Premium"
                      color="primary"
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  )}

                  <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
                    R$ {parseFloat(plan.price).toFixed(2)}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Por {plan.duration_days} dias
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body2" paragraph>
                    {plan.description}
                  </Typography>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      ✓ Destaque nas buscas
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ✓ Mais visibilidade
                    </Typography>
                    {plan.has_premium_badge && (
                      <Typography variant="body2" color="text.secondary">
                        ✓ Selo Premium
                      </Typography>
                    )}
                    {plan.appears_in_home && (
                      <Typography variant="body2" color="text.secondary">
                        ✓ Destaque na página inicial
                      </Typography>
                    )}
                  </Box>
                </CardContent>

                <CardActions>
                  <Button
                    fullWidth
                    variant={selectedPlan?.id === plan.id ? 'contained' : 'outlined'}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    {selectedPlan?.id === plan.id ? 'Selecionado' : 'Selecionar'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Método de Pagamento */}
        {selectedPlan && (
          <>
            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Método de Pagamento
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <FormControlLabel
                  value="pix"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <PixIcon />
                      <span>PIX (Aprovação Imediata)</span>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="boleto"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <BoletoIcon />
                      <span>Boleto Bancário</span>
                    </Box>
                  }
                  disabled
                />
              </RadioGroup>
            </FormControl>

            {/* Resumo */}
            <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Resumo do Pedido
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Plano:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedPlan.name}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Duração:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedPlan.duration_days} dias
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Método:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {paymentMethod === 'pix' ? 'PIX' : 'Boleto'}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" fontWeight={700}>
                  Total:
                </Typography>
                <Typography variant="h6" color="primary" fontWeight={700}>
                  R$ {parseFloat(selectedPlan.price).toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          </>
        )}

        {/* Botões de Ação */}
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate(-1)}
            disabled={creating}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleCreatePayment}
            disabled={!selectedPlan || creating}
            sx={{ flex: 1 }}
          >
            {creating ? 'Criando...' : 'Prosseguir para Pagamento'}
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}

export default BoostProperty
